import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-20d9bf0d"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "loading-spinner"
}
const _hoisted_2 = ["src"]
const _hoisted_3 = {
  key: 2,
  class: "warning-container"
}
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "dx-icon-warning" }, null, -1))
const _hoisted_5 = [
  _hoisted_4
]

import {
  PropType,
  computed,
  inject,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import { PropertyImageType } from "acquaint-api/constants/PropertyImageType";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import DxLoadIndicator from "devextreme-vue/load-indicator";
import PropertyAwaitingImageIcon from "@/components/icons/property-awaiting-image-icon.vue";
import { CarouselPictureType } from "@/enums/CarouselRecordType";


export default /*#__PURE__*/_defineComponent({
  __name: 'carousel-picture',
  props: {
  /**
   * The unique identifier for the image record.
   * Used to fetch and display the correct image.
   * @type {number}
   * @required
   */
  recordId: {
    type: Number,
    default: -1,
  },
  /**
   * The index number of the image in the property’s image list.
   * Determines which image should be displayed.
   * @type {number}
   * @required
   */
  pictureNumber: {
    type: Number,
    required: true,
  },
  /**
   * The type of the property image (e.g., main image, floor plan, etc.).
   * Uses a predefined enum `PropertyImageType` to specify the image category.
   * @type {PropertyImageType}
   * @required
   */
  type: {
    type: Number as PropType<PropertyImageType>,
    default: PropertyImageType.PropertyImageHighRes,
  },
  /**
   * Indicates whether the image should be updated when changes occur.
   * If `true`, the component will fetch and display the latest image.
   * @type {boolean}
   * @default false
   */
  updateImage: {
    type: Boolean,
    default: false,
  },

  /**
   * The width of the image container.
   * Controls how much horizontal space the image occupies.
   * @type {string}
   * @default '100%'
   */
  imageWidth: {
    type: String,
    default: "100%",
  },

  /**
   * The height of the image container.
   * Defines the vertical space the image should take.
   * @type {string}
   * @default 'unset'
   */
  imageHeight: {
    type: String,
    default: "unset",
  },

  /**
   * Defines how the image should be resized to fit its container.
   * Common values: `cover`, `contain`, `fill`, or `none`.
   * @type {string}
   * @default 'unset'
   */
  objectFit: {
    type: String,
    default: "unset",
  },

  /**
   * The maximum height the image can have.
   * Prevents images from exceeding a certain vertical size.
   * @type {string}
   * @default 'unset'
   */
  maxImageHeight: {
    type: String,
    default: "unset",
  },

  /**
   * The top and bottom padding around the image.
   * Helps adjust spacing without affecting width and height.
   * @type {string}
   * @default "0rem"
   */
  topBottomPadding: {
    type: String,
    default: "0rem",
  },
  /**
   * Determines the type of record the pictures belong to.
   */
  recordType: {
    type: Number as PropType<CarouselPictureType>,
    default: CarouselPictureType.Property,
  },
  /**
   * image url.
   * @type {String}
   * @default
   */
  imageUrl: {
    type: String,
    default: "",
  },
},
  emits: [
  /**
   * Triggered when there is an error while loading the picture.
   * This notifies the parent component that the image could not be loaded.
   * @event errorLoadingPicture
   */
  "errorLoadingPicture",

  /**
   * Triggered when the picture has been successfully loaded.
   * This informs the parent component that the image has finished loading.
   * @event loaded
   */
  "loaded",
],
  setup(__props, { emit }) {

const props = __props;



const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const loading = ref(false);
const pictureSource = ref("");
const errorLoadingPicture = ref(false);


onMounted(async () => {
  await renderImage();
});

watch(
  () => props.updateImage,
  () => {
    if (props.updateImage) {
      renderImage();
    }
  },
);

watch(
  () => props.recordId,
  (oldVal: number, newVal: number) => {
    if (oldVal === newVal) {
      return;
    }

    renderImage();
  },
);

watch(
  () => props.recordId,
  (oldVal: number, newVal: number) => {
    if (oldVal === newVal) {
      return;
    }

    renderImage();
  },
);

const renderImage = async () => {
  loading.value = true;

  try {
    switch (props.recordType) {
      case CarouselPictureType.MaintenanceRequest:
        pictureSource.value = props.imageUrl;
        break;
      default:
        pictureSource.value = windowUrl.value.createObjectURL(
          await apiClient.getFile(pictureApiUrl.value),
        );
        break;
    }
    emit("loaded");
  } catch (ex) {
    errorLoadingPicture.value = true;
    emit("errorLoadingPicture");
  }

  loading.value = false;
};

const pictureApiUrl = computed(() =>
  props.recordType == CarouselPictureType.Property
    ? `app/properties/${props.recordId}/pictures/${props.pictureNumber}?type=${props.type}`
    : `app/workorders/${props.recordId}/pictures/${props.pictureNumber}`,
);

onUnmounted(() => {
  // To ensure the picture is not left hanging in browser memory call revokeObjectUrl to delete the cached image
  if (pictureSource.value != "") {
    windowUrl.value.revokeObjectURL(pictureSource.value);
  }
});

// Webkit browser support webkitURl (chrome firefox) the other class caters for browsers that don't (not sure if there are any but did it just incase)
const windowUrl = computed(() => window.webkitURL || window.URL);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "picture-container",
    style: _normalizeStyle({
      '--image-width': props.imageWidth,
      '--image-height': props.imageHeight,
      '--max-image-height': props.maxImageHeight,
      '--object-fit': props.objectFit,
      '--top-bottom-padding': props.topBottomPadding,
    })
  }, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(PropertyAwaitingImageIcon, { class: "awaiting-image" }),
          _createVNode(_unref(DxLoadIndicator), {
            height: 40,
            width: 40
          })
        ]))
      : (pictureSource.value && !errorLoadingPicture.value)
        ? (_openBlock(), _createElementBlock("img", {
            key: 1,
            src: pictureSource.value
          }, null, 8, _hoisted_2))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, _hoisted_5))
  ], 4))
}
}

})